import {
  type BulkPackTakedownTarget,
  PACK_TAKEDOWN_BULK_MAX_TARGETS,
  type PublishedPack,
} from '@planuze/admin-contracts';
import {
  Button,
  InlineAlert,
  Page,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
  toast,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { PacksCatalogBulkBar } from '../components/packs-catalog/packs-catalog-bulk-bar.js';
import {
  applyCatalogFilters,
  type CatalogFilters,
  catalogFiltersActive,
  DEFAULT_CATALOG_FILTERS,
} from '../components/packs-catalog/packs-catalog-filter-model.js';
import { PacksCatalogFilters } from '../components/packs-catalog/packs-catalog-filters.js';
import { PacksCatalogTable } from '../components/packs-catalog/packs-catalog-table.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { usePacksCatalogListContent } from '../content/sections/packs-catalog-list.js';
import { useLimitedBulkSelection } from '../hooks/use-limited-bulk-selection.js';
import {
  useBulkTakedownPackVersions,
  usePublishedPacksList,
  useTakedownPackVersion,
} from '../hooks/use-packs-catalog.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

interface TakedownTarget {
  readonly kind: 'single';
  readonly packId: string;
  readonly version: string;
  readonly displayName: string;
}

interface BulkTakedownTarget {
  readonly kind: 'bulk';
  readonly targets: readonly BulkPackTakedownTarget[];
}

type PendingTakedown = TakedownTarget | BulkTakedownTarget;

/* Catálogo de packs publicados no registry (read-only, ADR-0400) + takedown de versão por
   staff (ADR-0464). Distinto da Moderação (fila de submissions community): aqui é o que JÁ
   está publicado/live. */
export function PacksCatalogListPage() {
  const content = usePacksCatalogListContent();
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePublishedPacksList();
  const navigate = useNavigate();
  const session = useStaffSession();
  const takedown = useTakedownPackVersion();
  const bulkTakedown = useBulkTakedownPackVersions();
  const [pending, setPending] = useState<PendingTakedown | null>(null);
  const [filters, setFilters] = useState<CatalogFilters>(DEFAULT_CATALOG_FILTERS);
  const packs = useMemo<readonly PublishedPack[]>(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );
  /* Takedown é destrutivo → só super_admin vê o botão (espelha o RBAC do admin-service). */
  const canTakedown = session !== null && session.role === 'super_admin';
  /* Catálogo paginado por cursor keyset (mais recentes primeiro no servidor). Busca + filtros +
     ordenação seguem client-side sobre as páginas JÁ carregadas (todos os campos existem no
     publishedPackDto, ADR-0704). Limitação assumida: como cada campo de ordenação (installs/nome)
     exigiria um keyset próprio por campo no registry, a ordenação/busca operam só sobre o que foi
     carregado — "Carregar mais" traz o restante em ordem de recência (server-side). */
  const filtered = useMemo(() => applyCatalogFilters(packs, filters), [packs, filters]);
  const visibleLiveIds = useMemo(
    () => filtered.filter((pack) => pack.latestActive !== null).map((pack) => pack.id),
    [filtered],
  );
  const selection = useLimitedBulkSelection(visibleLiveIds, PACK_TAKEDOWN_BULK_MAX_TARGETS);
  const hasFilters = catalogFiltersActive(filters);
  const updateFilters = (patch: Partial<CatalogFilters>): void =>
    setFilters((prev) => ({ ...prev, ...patch }));
  const clearFilters = (): void => setFilters(DEFAULT_CATALOG_FILTERS);
  const isBusy = takedown.isPending || bulkTakedown.isPending;

  const requestBulkTakedown = (): void => {
    const targets = filtered.flatMap((pack): BulkPackTakedownTarget[] => {
      if (!selection.selectedIds.has(pack.id) || pack.latestActive === null) return [];
      return [{ packId: pack.id, version: pack.latestActive }];
    });
    if (targets.length === 0) return;
    setPending({ kind: 'bulk', targets });
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{content.pageTitle}</PageTitle>
          <PageDescription>{content.pageDescription}</PageDescription>
        </PageTitleBlock>
      </PageHeader>
      <PageContent className="space-y-4">
        <PacksCatalogFilters
          packs={packs}
          filters={filters}
          onChange={updateFilters}
          resultCount={filtered.length}
          totalCount={packs.length}
        />
        {canTakedown && selection.selectedIds.size > 0 && (
          <PacksCatalogBulkBar
            selectedLabel={content.bulk.selectedLabel(selection.selectedIds.size)}
            actionLabel={content.bulk.actionLabel}
            clearLabel={content.bulk.clearLabel}
            busy={isBusy}
            onApply={requestBulkTakedown}
            onClear={selection.clear}
          />
        )}
        {isError ? (
          <InlineAlert variant="destructive">{content.loadError}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={canTakedown ? 9 : 7} />
        ) : (
          <PacksCatalogTable
            items={filtered}
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
            onRowClick={(id) => {
              navigate({ to: '/packs/$packId', params: { packId: id } }).catch(() => undefined);
            }}
            canTakedown={canTakedown}
            pendingPackId={pending?.kind === 'single' ? pending.packId : null}
            isTakingDown={isBusy}
            selectedIds={selection.selectedIds}
            onToggle={selection.toggle}
            onToggleAll={selection.toggleAll}
            onTakedown={(pack: PublishedPack) =>
              setPending({
                kind: 'single',
                packId: pack.id,
                version: pack.latestActive as string,
                displayName: pack.displayName,
              })
            }
          />
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              loading={isFetchingNextPage}
              onClick={() => {
                fetchNextPage().catch(() => undefined);
              }}
            >
              {content.loadMoreButton}
            </Button>
          </div>
        )}
      </PageContent>
      {pending?.kind === 'single' && (
        <ReasonDialog
          open
          onClose={() => !isBusy && setPending(null)}
          title={content.takedownDialogTitle(pending.displayName, pending.version)}
          description={content.takedownDialogDescription}
          confirmLabel={content.takedownConfirmLabel}
          reasonLabel={content.takedownReasonLabel}
          destructive
          pending={isBusy}
          validate={(reason) => reason.trim().length > 0}
          onConfirm={(reason) => {
            takedown.mutate(
              { packId: pending.packId, version: pending.version, reason },
              {
                onSuccess: () => {
                  toast.success(content.toastTakenDown);
                  setPending(null);
                },
                onError: () => toast.error(content.toastTakedownError),
              },
            );
          }}
        />
      )}
      {pending?.kind === 'bulk' && (
        <ReasonDialog
          open
          onClose={() => !isBusy && setPending(null)}
          title={content.bulk.dialogTitle(pending.targets.length)}
          description={content.bulk.dialogDescription}
          confirmLabel={content.bulk.actionLabel}
          reasonLabel={content.takedownReasonLabel}
          destructive
          pending={isBusy}
          validate={(reason) => reason.trim().length > 0}
          onConfirm={(reason) => {
            bulkTakedown.mutate(
              { targets: [...pending.targets], reason: reason.trim() },
              {
                onSuccess: (response) => {
                  const failed = response.results.filter((result) => !result.ok).length;
                  if (failed === 0) {
                    toast.success(content.bulk.successToast(response.results.length));
                  } else {
                    toast.warning(
                      content.bulk.partialToast(response.results.length - failed, failed),
                    );
                  }
                  selection.clear();
                  setPending(null);
                },
                onError: () => toast.error(content.toastTakedownError),
              },
            );
          }}
        />
      )}
    </Page>
  );
}
